import React from 'react'
import {Modal} from 'common/modals'
import {Button} from 'common/buttons'
import {useState, useModal, useDebounce, useQuery, useMutation} from 'hooks'
import {
	queryAdventureBackpack,
	queryGearCatalog,
	queryManufacturers,
	mutationUpdateBackpack,
	mutationCreateGear,
	mutationCreateManufacturer,
} from 'graph'

const GEAR_TYPES: {value: string; label: string}[] = [
	{value: 'CLOTHING', label: 'Clothing'},
	{value: 'TRANSPORT', label: 'Transport'},
	{value: 'CAMPING', label: 'Camping'},
	{value: 'COOKING', label: 'Cooking'},
	{value: 'NAVIGATION', label: 'Navigation'},
	{value: 'ELECTRONICS', label: 'Electronics'},
	{value: 'MISC', label: 'Other'},
]
const TYPE_LABEL: Record<string, string> = Object.fromEntries(
	GEAR_TYPES.map((t) => [t.value, t.label])
)

/** Grams → "740 g" / "3.54 kg". */
function formatWeight(grams: number): string {
	if (!grams) return '0 g'
	return grams < 1000 ? `${grams} g` : `${(grams / 1000).toFixed(2)} kg`
}

type Item = {gear: any; quantity: number}

/** Read-only rendering of a backpack's contents (shared by the card and the view modal). */
function BackpackContents({backpack, emptyText}: {backpack: any; emptyText: string}) {
	const items: Item[] = backpack?.items ?? []
	if (items.length === 0 && !backpack?.water && !backpack?.food) {
		return <p className="text-muted mb-0">{emptyText}</p>
	}
	return (
		<>
			<ul className="list-group list-group-flush mb-3">
				{items.map((it) => (
					<li
						key={it.gear.id}
						className="list-group-item d-flex justify-content-between px-0"
					>
						<span>
							{it.quantity > 1 && <span className="text-muted">{it.quantity}× </span>}
							{it.gear.name}
							<br />
							<small className="text-muted">
								{TYPE_LABEL[it.gear.type] ?? it.gear.type}
								{it.gear.manufacturer ? ` · ${it.gear.manufacturer.name}` : ''}
							</small>
						</span>
						<span className="text-muted">
							{it.gear.weight ? formatWeight(it.gear.weight * it.quantity) : '—'}
						</span>
					</li>
				))}
				{!!backpack?.water && (
					<li className="list-group-item d-flex justify-content-between px-0">
						<span>Water</span>
						<span className="text-muted">{backpack.water} ml</span>
					</li>
				)}
				{!!backpack?.food && (
					<li className="list-group-item d-flex justify-content-between px-0">
						<span>Food</span>
						<span className="text-muted">{backpack.food} g</span>
					</li>
				)}
			</ul>
			<div className="d-flex justify-content-between">
				<strong>Total weight</strong>
				<strong>{formatWeight(backpack?.totalWeight ?? 0)}</strong>
			</div>
		</>
	)
}

/** Sidebar card showing the viewer's own pack list for an adventure (participants only). */
export function BackpackCard({adventure}: {adventure: any}) {
	const {data} = useQuery(queryAdventureBackpack, {variables: {id: adventure.id}})
	const a = data?.adventure
	const backpack = a?.viewerBackpack
	const openEditor = useModal(BackpackModal, {adventureId: adventure.id, backpack})

	if (!a?.viewerIsParticipant) return null

	const items: Item[] = backpack?.items ?? []

	return (
		<div className="card">
			<div className="card-header d-flex justify-content-between align-items-center">
				<h4 className="card-header-title">Backpack</h4>
				<Button variant="outline-primary" size="sm" onClick={() => openEditor()}>
					{items.length ? 'Edit' : 'Pack'}
				</Button>
			</div>
			<div className="card-body">
				<BackpackContents
					backpack={backpack}
					emptyText="Build your pack for this adventure."
				/>
			</div>
		</div>
	)
}

/** Read-only modal showing a participant's backpack — for anyone who can read the adventure. */
export function BackpackViewModal(props: any) {
	const {backpack, user} = props
	return (
		<Modal {...props}>
			<div className="modal-body">
				<h2>
					<small className="text-muted">Backpack</small>
					<br />
					{user?.fullname ?? 'Participant'}
				</h2>
				<div className="mt-3">
					<BackpackContents backpack={backpack} emptyText="Nothing packed yet." />
				</div>
			</div>
		</Modal>
	)
}

/** A small form to contribute a new gear item (and optionally manufacturer) to the catalog. */
function NewGearForm({
	manufacturers,
	onCreated,
}: {
	manufacturers: any[]
	onCreated: (gear: any) => void
}) {
	const [open, setOpen] = useState(false)
	const [name, setName] = useState('')
	const [type, setType] = useState('MISC')
	const [weight, setWeight] = useState('')
	const [mfr, setMfr] = useState('')

	const [createManufacturer] = useMutation(mutationCreateManufacturer, {
		refetchQueries: [{query: queryManufacturers, variables: {limit: 100}}],
	})
	const [createGear, {loading}] = useMutation(mutationCreateGear)

	const submit = async () => {
		if (!name.trim()) return
		let manufacturerId: string | null = null
		const mfrName = mfr.trim()
		if (mfrName) {
			const found = manufacturers.find(
				(m) => m.name.toLowerCase() === mfrName.toLowerCase()
			)
			if (found) manufacturerId = found.id
			else {
				const r = await createManufacturer({variables: {input: {name: mfrName}}})
				manufacturerId = r.data?.createManufacturer?.manufacturer?.id ?? null
			}
		}
		const r = await createGear({
			variables: {
				input: {
					name: name.trim(),
					type,
					weight: weight ? Number(weight) : null,
					manufacturerId,
				},
			},
		})
		const gear = r.data?.createGear?.gear
		if (gear) onCreated(gear)
		setName('')
		setWeight('')
		setMfr('')
		setType('MISC')
		setOpen(false)
	}

	if (!open) {
		return (
			<button className="btn btn-link p-0 mt-2" onClick={() => setOpen(true)}>
				＋ Missing something? Add a new item
			</button>
		)
	}

	return (
		<div className="border rounded p-2 mt-2">
			<input
				className="form-control form-control-sm mb-2"
				placeholder="Name (e.g. brand + model)"
				value={name}
				onChange={(e) => setName(e.target.value)}
			/>
			<div className="row g-2 mb-2">
				<div className="col">
					<select
						className="form-select form-select-sm"
						value={type}
						onChange={(e) => setType(e.target.value)}
					>
						{GEAR_TYPES.map((t) => (
							<option key={t.value} value={t.value}>
								{t.label}
							</option>
						))}
					</select>
				</div>
				<div className="col">
					<input
						type="number"
						className="form-control form-control-sm"
						placeholder="Weight (g)"
						value={weight}
						onChange={(e) => setWeight(e.target.value)}
					/>
				</div>
			</div>
			<input
				className="form-control form-control-sm mb-2"
				placeholder="Manufacturer (optional)"
				list="mfr-list"
				value={mfr}
				onChange={(e) => setMfr(e.target.value)}
			/>
			<datalist id="mfr-list">
				{manufacturers.map((m) => (
					<option key={m.id} value={m.name} />
				))}
			</datalist>
			<div className="d-flex justify-content-end gap-2">
				<Button variant="outline-secondary" size="sm" onClick={() => setOpen(false)}>
					Cancel
				</Button>
				<Button
					variant="outline-success"
					size="sm"
					disabled={loading || !name.trim()}
					onClick={submit}
				>
					Create & add
				</Button>
			</div>
		</div>
	)
}

/** The pack editor: browse/extend the catalog, add gear with quantities, set water/food. */
export function BackpackModal(props: any) {
	const {adventureId, backpack, close} = props

	const [items, setItems] = useState<Record<string, Item>>(() =>
		Object.fromEntries((backpack?.items ?? []).map((it: Item) => [it.gear.id, it]))
	)
	const [description, setDescription] = useState<string>(backpack?.description ?? '')
	const [water, setWater] = useState<string>(backpack?.water != null ? String(backpack.water) : '')
	const [food, setFood] = useState<string>(backpack?.food != null ? String(backpack.food) : '')

	const [keyword, setKeyword] = useState('')
	const search = useDebounce(keyword, 300)
	const [type, setType] = useState<string | null>(null)

	const {data, loading, refetch} = useQuery(queryGearCatalog, {
		variables: {keyword: search || null, type, limit: 100},
	})
	const catalog: any[] = data?.gear ?? []

	const {data: mfrData} = useQuery(queryManufacturers, {variables: {limit: 100}})
	const manufacturers: any[] = mfrData?.manufacturers ?? []

	const setQuantity = (gear: any, quantity: number) =>
		setItems((prev) => {
			const next = {...prev}
			if (quantity <= 0) delete next[gear.id]
			else next[gear.id] = {gear, quantity}
			return next
		})

	const list = Object.values(items)
	const totalWeight =
		list.reduce((sum, it) => sum + (it.gear.weight ?? 0) * it.quantity, 0) +
		(Number(water) || 0) +
		(Number(food) || 0)

	const [save, {loading: saving}] = useMutation(mutationUpdateBackpack, {
		refetchQueries: [{query: queryAdventureBackpack, variables: {id: adventureId}}],
		onCompleted: () => close(),
		onError: (e: any) => console.log(e),
	})

	const onSave = () =>
		save({
			variables: {
				input: {
					adventureId,
					description: description || null,
					water: water ? Number(water) : null,
					food: food ? Number(food) : null,
					items: list.map((it) => ({gearId: it.gear.id, quantity: it.quantity})),
				},
			},
		})

	return (
		<Modal {...props}>
			<div className="modal-body">
				<h2>
					<small className="text-muted">Backpack</small>
					<br />
					Pack your gear
				</h2>

				<div className="row mt-3">
					{/* Catalog browser */}
					<div className="col-md-7 mb-3">
						<input
							className="form-control mb-2"
							placeholder="Search gear…"
							value={keyword}
							onChange={(e) => setKeyword(e.target.value)}
						/>
						<div className="mb-2">
							<button
								className={`btn btn-sm me-1 mb-1 ${type === null ? 'btn-primary' : 'btn-outline-secondary'}`}
								onClick={() => setType(null)}
							>
								All
							</button>
							{GEAR_TYPES.map((t) => (
								<button
									key={t.value}
									className={`btn btn-sm me-1 mb-1 ${type === t.value ? 'btn-primary' : 'btn-outline-secondary'}`}
									onClick={() => setType(t.value)}
								>
									{t.label}
								</button>
							))}
						</div>
						<ul className="list-group" style={{maxHeight: 320, overflowY: 'auto'}}>
							{loading && catalog.length === 0 && (
								<li className="list-group-item text-muted">Loading…</li>
							)}
							{!loading && catalog.length === 0 && (
								<li className="list-group-item text-muted">No gear found.</li>
							)}
							{catalog.map((g) => (
								<li
									key={g.id}
									className="list-group-item d-flex justify-content-between align-items-center"
								>
									<span>
										{g.name}
										<br />
										<small className="text-muted">
											{TYPE_LABEL[g.type] ?? g.type}
											{g.manufacturer ? ` · ${g.manufacturer.name}` : ''}
											{g.weight ? ` · ${formatWeight(g.weight)}` : ''}
										</small>
									</span>
									<Button
										variant="outline-success"
										size="sm"
										onClick={() => setQuantity(g, (items[g.id]?.quantity ?? 0) + 1)}
									>
										+
									</Button>
								</li>
							))}
						</ul>
						<NewGearForm
							manufacturers={manufacturers}
							onCreated={(gear) => {
								setQuantity(gear, (items[gear.id]?.quantity ?? 0) + 1)
								refetch()
							}}
						/>
					</div>

					{/* Selected items + meta */}
					<div className="col-md-5">
						<h4>Your backpack</h4>
						{list.length === 0 && <p className="text-muted">Nothing added yet.</p>}
						<ul className="list-group list-group-flush mb-2">
							{list.map((it) => (
								<li
									key={it.gear.id}
									className="list-group-item px-0 d-flex justify-content-between align-items-center"
								>
									<span className="me-2">{it.gear.name}</span>
									<span className="text-nowrap">
										<button
											className="btn btn-sm btn-outline-secondary"
											onClick={() => setQuantity(it.gear, it.quantity - 1)}
										>
											−
										</button>
										<span className="mx-2">{it.quantity}</span>
										<button
											className="btn btn-sm btn-outline-secondary"
											onClick={() => setQuantity(it.gear, it.quantity + 1)}
										>
											+
										</button>
									</span>
								</li>
							))}
						</ul>
						<div className="row g-2 mb-2">
							<div className="col">
								<label className="form-label small text-muted mb-0">Water (ml)</label>
								<input
									type="number"
									className="form-control"
									value={water}
									onChange={(e) => setWater(e.target.value)}
								/>
							</div>
							<div className="col">
								<label className="form-label small text-muted mb-0">Food (g)</label>
								<input
									type="number"
									className="form-control"
									value={food}
									onChange={(e) => setFood(e.target.value)}
								/>
							</div>
						</div>
						<textarea
							className="form-control mb-2"
							placeholder="Note (optional)"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
						<div className="d-flex justify-content-between">
							<strong>Total weight</strong>
							<strong>{formatWeight(totalWeight)}</strong>
						</div>
					</div>
				</div>
			</div>
			<div className="modal-footer">
				<Button variant="outline-secondary" onClick={close}>
					Cancel
				</Button>
				<Button variant="outline-success" disabled={saving} onClick={onSave}>
					Save
				</Button>
			</div>
		</Modal>
	)
}
